import { route, SandboxError, type Route } from '../adapter';
import { newId, now, type SandboxStore } from '../store';
import { DEV_STAGE, todayIso, type DevFeature, type DevFeatureInput, type DevStage, type DevStageEvent } from '@/lib/dev-tracker';

/**
 * The development tracker. A move sets the stage and the day it was reached,
 * which may be in the past but not the future, and is added to the history
 * in the order it was recorded, as the API's `moveStage` does.
 */

const RECORDED_BY = 'You';

function featureOr404(store: SandboxStore, id: string): DevFeature {
  const found = store.portal.features.find((feature) => feature.id === id);
  if (!found) throw new SandboxError(404, 'Feature not found.');
  return found;
}

/** The feature with one more move, its stage, dates and live day worked out again. */
export function withMove(feature: DevFeature, move: { stage: DevStage; on: string; note?: string }, today: string = todayIso()): DevFeature {
  if (move.on > today) throw new SandboxError(400, 'Pick today or an earlier day.');
  const event: DevStageEvent = { stage: move.stage, on: move.on, recordedBy: RECORDED_BY, recordedAt: now(), ...(move.note ? { note: move.note } : {}) };
  const stageDates: Partial<Record<DevStage, string>> = { ...feature.stageDates, [move.stage]: move.on };
  return { ...feature, history: [...feature.history, event], stage: move.stage, stageSince: move.on, stageDates, liveOn: stageDates[DEV_STAGE.live] ?? null };
}

function replace(store: SandboxStore, feature: DevFeature): DevFeature {
  store.portal.features = store.portal.features.map((row) => (row.id === feature.id ? feature : row));
  return feature;
}

export const devTrackerRoutes: readonly Route[] = [
  route('GET', '/dev-tracker/features', ({ store }) => ({ data: store.portal.features })),
  route('POST', '/dev-tracker/features', ({ store, body }) => {
    const input = body as DevFeatureInput & { stage: DevStage; on: string };
    if (input.title.trim() === '') throw new SandboxError(400, 'Name the feature.');
    const blank: DevFeature = { id: newId(), title: input.title, summary: input.summary, area: input.area, links: input.links, stage: input.stage, stageSince: null, liveOn: null, stageDates: {}, history: [], createdAt: now() };
    const feature = withMove(blank, { stage: input.stage, on: input.on });
    store.portal.features = [...store.portal.features, feature];
    return { data: feature };
  }),
  route('PATCH', '/dev-tracker/features/:id', ({ store, params, body }) => ({ data: replace(store, { ...featureOr404(store, params['id'] ?? ''), ...(body as Partial<DevFeatureInput>) }) })),
  route('POST', '/dev-tracker/features/:id/stage', ({ store, params, body }) => ({ status: 200, data: replace(store, withMove(featureOr404(store, params['id'] ?? ''), body as { stage: DevStage; on: string; note?: string })) })),
  route('DELETE', '/dev-tracker/features/:id', ({ store, params }) => {
    store.portal.features = store.portal.features.filter((feature) => feature.id !== params['id']);
    return { data: null };
  }),
];
